import React, { useEffect } from "react";
import "./index.scss";
import {
  setResponsiveImage,
  checkImage,
  imgCrop,
  reg,
  brandColours,
  getHexColor
} from "../../helpers";

interface tournamentProps {
  index?: number;
  featured?: boolean;
  name: string;
  surfaceDesc: string | null;
  liveStreamingUrl: string | null;
  prizeMoney: string | number | null;
  hostNationCode: string;
  startDate: string;
  endDate: string;
  orderofPlayLink?: string;
  tourCategoryDesc: string | null;
  tourCategoryCode: string | null;
  tournamentLink: string;
  imageUrls: imgCrop[] | null;
  tournamentId: number;
  surfaceCode: string | null;
  backgroundColour?: string;
}

const SingleTournament = ({
  index,
  name,
  featured = false,
  surfaceDesc,
  liveStreamingUrl = null,
  hostNationCode = "GBR",
  imageUrls,
  tournamentLink,
  tournamentId,
  surfaceCode,
  tourCategoryCode,
  tourCategoryDesc,
  backgroundColour
}: tournamentProps) => {
  useEffect(() => {
    setResponsiveImage({
      mobileImage:
        featured && imageUrls
          ? checkImage({ images: imageUrls, size: "Small" })
          : "no-mobile-image",
      tabletImage:
        featured && imageUrls
          ? checkImage({ images: imageUrls, size: "Medium" })
          : "no-tablet-image",
      desktopImage:
        featured && imageUrls
          ? checkImage({ images: imageUrls, size: "Large" })
          : "no-desktop-image",
      elClassName: `latest-tournaments__image--${tournamentId}--${index}${
        featured ? "--featured" : ""
      }`
    });
  });

  const tourLabel = tourCategoryCode || "";
  const tourString = name.split(" ")[0];
  const tourCategoryDes = tourCategoryDesc || "";

  const AllRegex = [reg.juniors, reg.beach, reg.masters, reg.Mens, reg.Womens];
  const getTourLabel = () => {
    if (AllRegex.some(reg => tourString.match(reg))) {
      const thisReg = AllRegex.filter(reg => tourString.match(reg))[0];
      return tourString ? tourString.match(thisReg) : null;
    } else if (AllRegex.some(reg => tourCategoryDes.match(reg))) {
      return tourCategoryDesc;
    } else if (AllRegex.some(reg => tourLabel.match(reg))) {
      return tourCategoryDesc;
    }
    return "";
  };
  const logoType = tourLabel ? tourLabel.substr(0, 1) : "";
  const brandColour = (() => {
    if (
      brandColours.blue.some(l => l == tourLabel || tourString.indexOf(l) > -1)
    ) {
      return "blue";
    } else if (
      brandColours.red.some(l => l == tourLabel || tourString.indexOf(l) > -1)
    ) {
      return "red";
    } else if (reg.juniors.test(tourLabel) || reg.juniors.test(tourString)) {
      return "green";
    } else if (
      reg.masters.test(tourCategoryDes) ||
      reg.masters.test(tourString)
    ) {
      return "purple";
    } else if (reg.beach.test(tourLabel) || reg.beach.test(tourString)) {
      return "beach";
    }
    return "";
  })();

  return (
    <div
      className={`latest-tournaments__single ${featured ? "featured" : ""}`}
      key={index}
    >
      {featured ? (
        <a
          href={tournamentLink}
          className={`latest-tournaments__image latest-tournaments__image--${tournamentId}--${index}${
            featured ? "--featured" : ""
          }`}
          style={
            backgroundColour
              ? {
                  borderColor: getHexColor(backgroundColour)
                }
              : {}
          }
        >
          <span
            style={
              backgroundColour
                ? {
                    borderColor: getHexColor(backgroundColour)
                  }
                : {}
            }
            className="latest-tournaments__label-feature"
          ></span>
        </a>
      ) : null}
      <div
        className={`latest-tournaments__info${
          brandColour ? ` ${brandColour}` : " unbranded"
        }`}
        style={
          backgroundColour
            ? {
                borderColor: getHexColor(backgroundColour)
              }
            : {}
        }
      >
        <div
          className={`latest-tournaments__branding`}
          style={
            backgroundColour
              ? {
                  borderColor: getHexColor(backgroundColour)
                }
              : {}
          }
        >
          <a
            href={tournamentLink}
            className={`latest-tournaments__branding-logo${
              logoType ? ` ${logoType}` : ""
            }`}
          >
            <span className="latest-tournaments__branding-logo-label">
              {getTourLabel()}
            </span>
          </a>
        </div>
        <div
          className="latest-tournaments__details"
          style={
            backgroundColour
              ? {
                  backgroundColor: getHexColor(backgroundColour)
                }
              : {}
          }
        >
          <div
            className="latest-tournaments__title"
            style={
              backgroundColour
                ? {
                    borderColor: getHexColor(backgroundColour)
                  }
                : {}
            }
          >
            <a href={tournamentLink}>{name}</a>
            {liveStreamingUrl ? (
              <a
                href={`${liveStreamingUrl}`}
                className="latest-tournaments__live-link"
              >
                Live
              </a>
            ) : null}
          </div>
          <div
            className="latest-tournaments__lower"
            style={
              backgroundColour
                ? {
                    backgroundColor: getHexColor(backgroundColour)
                  }
                : {}
            }
          >
            <div className={`latest-tournaments__surface`}>
              {surfaceDesc ? (
                <>
                  <span className="latest-tournaments__surface-desc">{`${surfaceDesc} (${surfaceCode})`}</span>
                  <span
                    className={`latest-tournaments__surface-icon latest-tournaments__surface--${surfaceCode}`}
                  ></span>
                </>
              ) : (
                "N/A"
              )}
            </div>
            <div className="latest-tournaments__nation">
              <span className="latest-tournaments__nation-code">
                {hostNationCode}
              </span>
              <span className={`itf-flags itf-flags--${hostNationCode}`}></span>
            </div>
            {liveStreamingUrl ? (
              <div className="latest-tournaments__badges">
                <a
                  href={`${liveStreamingUrl}`}
                  className="latest-tournaments__live-link"
                >
                  Live
                </a>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ({
  latestTournaments,
  featuredTournaments,
  backgroundColour
}: {
  latestTournaments: tournamentProps[];
  featuredTournaments: tournamentProps[];
  backgroundColour?: string;
}) => {
  const idsFeatured = featuredTournaments.map(
    ({ tournamentId }) => tournamentId
  );
  const tournaments = latestTournaments.filter(
    ({ tournamentId }) => !idsFeatured.includes(tournamentId)
  );
  const circuitReg = /^[BT|M|W|J|JT|MT|WCT]+/gi;
  const importantEventReg = /^[Grade 1|GA|Grand Slam|CHAMP]+/gi;
  const isImportantEvent = (b: tournamentProps) =>
    importantEventReg.test(b.tourCategoryDesc || "") ||
    importantEventReg.test(b.tourCategoryCode || "");
  const getGradeValue = (b: tournamentProps) => {
    const str = b.name.split(" ")[0];
    return parseInt(str.replace(circuitReg, ""));
  };
  const sorted = (a: tournamentProps, b: tournamentProps) => {
    if (isImportantEvent(a) || isImportantEvent(b)) {
      return 1;
    }
    if (isNaN(getGradeValue(b)) || isNaN(getGradeValue(a))) {
      return -1;
    }
    return getGradeValue(b) - getGradeValue(a);
  };

  const setSlice = () => {
    switch (featuredTournaments.length) {
      case 1:
        return 4;
        break;
      case 2:
        return 2;
        break;
      case 3:
        return 0;
        break;
    }
    return 6;
  };

  return (
    <div
      className={`latest-tournaments version--2${
        featuredTournaments.length === 1 ? " featured-label" : ""
      }`}
    >
      {featuredTournaments
        ? featuredTournaments.map((feature: tournamentProps, index: number) => (
            <SingleTournament
              key={feature.tournamentId}
              index={index}
              backgroundColour={backgroundColour}
              featured
              {...feature}
            />
          ))
        : null}
      {tournaments
        .sort(sorted)
        .slice(0, setSlice())
        .map(tournament => (
          <SingleTournament
            key={tournament.tournamentId}
            featured={false}
            backgroundColour={backgroundColour}
            index={tournament.tournamentId}
            {...tournament}
          />
        ))}
    </div>
  );
};
